import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { fmtDate } from "../lib/ui";
import { Plus, Upload, Download, Database, Trash2, Shield, Pencil, Check, X } from "lucide-react";
import BrandingCRM from "./BrandingCRM";
import AccessControl from "./AccessControl";
import Integrations from "./Integrations";
import { AccountsAdmin, SignupSettings, AccountTypesAdmin, VisibilitySettings } from "./PlatformSettings";
import Templates from "./Templates";
import Automations from "./Automations";
import { DEFAULT_TYPOGRAPHY, TYPO_GROUPS, FONT_OPTIONS, applyTypography } from "../lib/typography";

export default function Admin() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const defaultTab = new URLSearchParams(window.location.search).has("calendar") ? "integrations" : "users";
  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Administration" subtitle="Users, organization masters, settings & system" />
      <Tabs defaultValue={defaultTab}>
        <TabsList className="flex-wrap h-auto justify-start mb-4">
          {["users", "accounts", "signup-settings", "account-types", "departments", "teams", "roles", "access-control", "approval-workflows", "escalation-rules", "recurring", "templates", "automations", "sla", "holidays", "lookups", "integrations", "audit", "import-export", "backup", "branding-crm", ...(isMaster ? ["visibility-settings"] : []), "settings"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize">{t.replace(/-/g, " ")}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="accounts"><AccountsAdmin /></TabsContent>
        <TabsContent value="signup-settings"><SignupSettings /></TabsContent>
        <TabsContent value="account-types"><AccountTypesAdmin /></TabsContent>
        <TabsContent value="departments"><Masters kind="departments" title="Departments" /></TabsContent>
        <TabsContent value="teams"><Masters kind="teams" title="Teams" /></TabsContent>
        <TabsContent value="roles"><Roles /></TabsContent>
        <TabsContent value="escalation-rules"><EscalationRules /></TabsContent>
        <TabsContent value="approval-workflows"><ApprovalWorkflows /></TabsContent>
        <TabsContent value="recurring"><Recurring /></TabsContent>
        <TabsContent value="templates"><Templates /></TabsContent>
        <TabsContent value="automations"><Automations /></TabsContent>
        <TabsContent value="sla"><SlaRules /></TabsContent>
        <TabsContent value="holidays"><Holidays /></TabsContent>
        <TabsContent value="lookups"><Lookups /></TabsContent>
        <TabsContent value="audit"><Audit /></TabsContent>
        <TabsContent value="import-export"><ImportExport /></TabsContent>
        <TabsContent value="backup"><Backup isMaster={isMaster} /></TabsContent>
        <TabsContent value="branding-crm"><BrandingCRM /></TabsContent>
        {isMaster && <TabsContent value="visibility-settings"><VisibilitySettings /></TabsContent>}
        <TabsContent value="access-control"><AccessControl /></TabsContent>
        <TabsContent value="integrations"><Integrations /></TabsContent>
        <TabsContent value="settings"><Settings /></TabsContent>
      </Tabs>
    </div>
  );
}

function Users() {
  const { users, departments, reload } = useData();
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const toggle = async (u) => { await api.patch(`/users/${u.id}/status`); toast.success("Updated"); reload(); };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.full_name}? This permanently removes the user and cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={() => { setEdit(null); setShow(true); }} data-testid="new-user-button"><Plus size={16} className="mr-1" /> New User</Button></div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Department</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b last:border-0" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2.5 font-medium">{u.full_name}</td>
                <td className="px-4 py-2.5 text-slate-500">{u.email}</td>
                <td className="px-4 py-2.5 capitalize">{u.role?.replace("_", " ")}</td>
                <td className="px-4 py-2.5">{departments.find((d) => d.id === u.department_id)?.name || "—"}</td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-2.5 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEdit(u); setShow(true); }}>Edit</Button>
                  {u.role !== "master_admin" && <Button variant="ghost" size="sm" onClick={() => toggle(u)}>{u.active ? "Deactivate" : "Activate"}</Button>}
                  {u.role !== "master_admin" && <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.email}`} onClick={() => del(u)}>Delete</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <UserModal user={edit} departments={departments} onClose={() => setShow(false)} onDone={() => { setShow(false); reload(); }} />}
    </div>
  );
}

function UserModal({ user, departments, onClose, onDone }) {
  const [f, setF] = useState(user || { full_name: "", email: "", role: "employee", department_id: "", designation: "", mobile: "", employee_id: "", password: "" });
  const submit = async () => {
    if (!f.full_name || !f.email) return toast.error("Name and email required");
    const payload = { ...f }; if (!payload.department_id) payload.department_id = null;
    try {
      if (user) await api.put(`/users/${user.id}`, payload);
      else await api.post("/users", payload);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="user-modal">
        <DialogHeader><DialogTitle>{user ? "Edit" : "New"} User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full Name *</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></div>
            <div><Label>Employee ID</Label><Input value={f.employee_id || ""} onChange={(e) => setF({ ...f, employee_id: e.target.value })} /></div>
          </div>
          <div><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["master_admin","admin","manager","employee","viewer"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Department</Label>
              <Select value={f.department_id || ""} onValueChange={(v) => setF({ ...f, department_id: v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Designation</Label><Input value={f.designation || ""} onChange={(e) => setF({ ...f, designation: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={f.mobile || ""} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
          </div>
          {!user && <div><Label>Temp Password (default ChangeMe@123)</Label><Input value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="ChangeMe@123" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Weekly Capacity (hrs)</Label><Input type="number" value={f.weekly_capacity_hours ?? 40} onChange={(e) => setF({ ...f, weekly_capacity_hours: Number(e.target.value) })} /></div>
            <div><Label>Working Days</Label><Input type="number" value={f.working_days ?? 5} onChange={(e) => setF({ ...f, working_days: Number(e.target.value) })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="user-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Masters({ kind, title }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [kind]);
  const add = async () => { if (!name) return; await api.post(`/${kind}`, { name }); setName(""); toast.success("Added"); load(); };
  const rename = async (item) => {
    if (!editing?.name?.trim()) return toast.error("Name required");
    const body = { ...item, name: editing.name.trim() };
    delete body.id; delete body._id; delete body.created_at;
    await api.put(`/${kind}/${item.id}`, body);
    toast.success("Renamed"); setEditing(null); load();
  };
  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try { await api.delete(`/${kind}/${item.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  return (
    <div className="max-w-xl">
      <div className="flex gap-2 mb-3"><Input placeholder={`New ${title.slice(0, -1)} name`} value={name} onChange={(e) => setName(e.target.value)} data-testid={`master-${kind}-input`} /><Button onClick={add} data-testid={`master-${kind}-add`}>Add</Button></div>
      <div className="bg-white border rounded-xl divide-y">
        {items.map((i) => (
          <div key={i.id} className="px-4 py-2.5 text-sm flex items-center gap-2" data-testid={`master-${kind}-row-${i.id}`}>
            {editing?.id === i.id ? (
              <>
                <Input className="h-8 flex-1" value={editing.name} autoFocus data-testid={`master-${kind}-rename-input`}
                  onChange={(e) => setEditing({ id: i.id, name: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && rename(i)} />
                <button onClick={() => rename(i)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md" data-testid={`master-${kind}-rename-save`}><Check size={15} /></button>
                <button onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md"><X size={15} /></button>
              </>
            ) : (
              <>
                <span className="flex-1">{i.name}</span>
                <button onClick={() => setEditing({ id: i.id, name: i.name })} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Rename" data-testid={`master-${kind}-edit`}><Pencil size={15} /></button>
                <button onClick={() => del(i)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Delete" data-testid={`master-${kind}-delete`}><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-sm">None yet.</div>}
      </div>
    </div>
  );
}

function Roles() {
  const [roles, setRoles] = useState({});
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data)); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {Object.entries(roles).map(([role, perms]) => (
        <div key={role} className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><Shield size={15} className="text-blue-600" /><span className="font-semibold capitalize">{role.replace("_", " ")}</span></div>
          <div className="flex flex-wrap gap-1.5">
            {perms.map((p) => <span key={p} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Lookups() {
  const [lookups, setLookups] = useState([]);
  const load = () => api.get("/lookups").then((r) => setLookups(r.data));
  useEffect(() => { load(); }, []);
  const cats = [...new Set(lookups.map((l) => l.category))];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {cats.map((c) => (
        <div key={c} className="bg-white border rounded-xl p-4">
          <div className="font-semibold capitalize text-slate-700 mb-2">{c.replace("_", " ")}</div>
          <div className="space-y-1">
            {lookups.filter((l) => l.category === c).map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-sm">
                {l.color && <span className="h-3 w-3 rounded-full" style={{ backgroundColor: l.color }} />}
                {l.label}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit-logs", { params: { limit: 200 } }).then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <th className="px-4 py-2.5">Time</th><th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Action</th><th className="px-4 py-2.5">Entity</th>
        </tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b last:border-0">
              <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(l.timestamp)}</td>
              <td className="px-4 py-2">{l.user_name}</td>
              <td className="px-4 py-2 capitalize">{l.action?.replace(/_/g, " ")}</td>
              <td className="px-4 py-2 text-slate-500">{l.entity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportExport() {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const doPreview = async (e) => {
    const f = e.target.files[0]; if (!f) return; setFile(f);
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPreview(data);
  };
  const doImport = async () => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/import/tasks", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`${data.created} tasks imported`); setPreview(null); setFile(null);
  };
  const exportTasks = async (fmt) => {
    const res = await api.get("/export/tasks", { params: { fmt }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `tasks.${fmt === "excel" ? "xlsx" : "csv"}`; a.click();
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
      <div className="bg-white border rounded-xl p-5">
        <div className="font-semibold text-slate-700 mb-2 flex items-center gap-2"><Upload size={16} /> Import Tasks (CSV)</div>
        <p className="text-xs text-slate-500 mb-3">CSV columns: title, description, priority, execution_status, current_due_date, progress</p>
        <label className="flex items-center gap-2 text-sm border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-50" data-testid="import-file">
          <Upload size={16} /> Choose CSV file
          <input type="file" accept=".csv" className="hidden" onChange={doPreview} />
        </label>
        {preview && (
          <div className="mt-3 text-sm">
            <div className="flex gap-3"><span className="text-emerald-600">{preview.valid} valid</span><span className="text-red-600">{preview.errors.length} errors</span><span className="text-slate-500">{preview.total} rows</span></div>
            {preview.errors.slice(0, 5).map((e, i) => <div key={i} className="text-xs text-red-500">Row {e.row}: {e.error}</div>)}
            <Button className="mt-3 w-full" onClick={doImport} disabled={preview.valid === 0} data-testid="import-confirm">Confirm Import ({preview.valid})</Button>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-xl p-5">
        <div className="font-semibold text-slate-700 mb-2 flex items-center gap-2"><Download size={16} /> Export Tasks</div>
        <p className="text-xs text-slate-500 mb-3">Exports respect your data scope & permissions.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportTasks("csv")}>Download CSV</Button>
          <Button variant="outline" onClick={() => exportTasks("excel")}>Download Excel</Button>
        </div>
      </div>
    </div>
  );
}

function Backup({ isMaster }) {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/backup/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => { await api.post("/backup/run"); toast.success("Backup completed"); load(); };
  const clearDemo = async () => {
    if (!window.confirm("This clears ALL tasks and operational data. Continue?")) return;
    try { await api.post("/demo/clear"); toast.success("Demo data cleared"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!status) return <div className="text-slate-400 text-sm">Loading…</div>;
  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3"><Database size={16} className="text-blue-600" /><span className="font-semibold">Database Health</span>
          <span className="ml-auto text-xs bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">{status.database_health}</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
          {Object.entries(status.collections).map(([k, v]) => (
            <div key={k}><div className="text-lg font-bold font-mono">{v}</div><div className="text-[10px] text-slate-400 capitalize">{k}</div></div>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-3">Last backup: {status.last_backup ? fmtDate(status.last_backup) : "Never"}</div>
        <Button className="mt-3" onClick={run} data-testid="run-backup">Run Backup</Button>
      </div>
      {isMaster && (
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2 text-red-700"><Trash2 size={16} /><span className="font-semibold">Danger Zone</span></div>
          <p className="text-sm text-slate-500 mb-3">Clear all demo and operational data (users, departments and settings are preserved).</p>
          <Button variant="destructive" onClick={clearDemo} data-testid="clear-demo">Clear Demo Data</Button>
        </div>
      )}
    </div>
  );
}

function EscalationRules() {
  const { users, departments } = useData();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: "", trigger_type: "overdue_days", trigger_threshold: 1, escalation_level: 2, recipient_role: "manager" });
  const load = () => api.get("/escalation-rules").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name) return toast.error("Rule name required");
    await api.post("/escalation-rules", { ...f, trigger_threshold: Number(f.trigger_threshold), escalation_level: Number(f.escalation_level) });
    toast.success("Rule added"); setF({ ...f, name: "" }); load();
  };
  const del = async (id) => { await api.delete(`/escalation-rules/${id}`); load(); };
  const TRIGGERS = ["due_today","overdue_days","critical_overdue","commitment_missed","blocked_days","no_update_days","repeated_commitment","repeated_due_date","approval_sla"];
  const ROLES = ["owner","manager","department_head","business_head","director"];
  return (
    <div>
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="escalation-rule-form">
        <div className="md:col-span-2"><Label>Rule Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Trigger</Label><SelBox v={f.trigger_type} set={(v) => setF({ ...f, trigger_type: v })} opts={TRIGGERS} /></div>
        <div><Label>Threshold</Label><Input type="number" value={f.trigger_threshold} onChange={(e) => setF({ ...f, trigger_threshold: e.target.value })} /></div>
        <div><Label>Level</Label><Input type="number" value={f.escalation_level} onChange={(e) => setF({ ...f, escalation_level: e.target.value })} /></div>
        <div><Label>Recipient</Label><SelBox v={f.recipient_role} set={(v) => setF({ ...f, recipient_role: v })} opts={ROLES} /></div>
        <Button className="md:col-span-6" onClick={add} data-testid="escalation-rule-add">Add Rule</Button>
      </div>
      <div className="bg-white border rounded-xl divide-y">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 text-sm flex items-center gap-3">
            <span className="font-medium">{r.name}</span>
            <span className="text-xs text-slate-500">{r.trigger_type} ≥ {r.trigger_threshold} → L{r.escalation_level} · {r.recipient_role}</span>
            <span className={`ml-auto text-xs rounded-full px-2 py-0.5 ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.active ? "Active" : "Off"}</span>
            <button className="text-slate-400 hover:text-red-600" onClick={() => del(r.id)}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalWorkflows() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [steps, setSteps] = useState([{ sequence: 1, approver_role: "manager", sla_days: 2, mandatory: true, mode: "sequential" }]);
  const load = () => api.get("/approval-workflows").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name) return toast.error("Template name required");
    await api.post("/approval-workflows", { name, steps });
    toast.success("Workflow created"); setName(""); setSteps([{ sequence: 1, approver_role: "manager", sla_days: 2, mandatory: true, mode: "sequential" }]); load();
  };
  const del = async (id) => { await api.delete(`/approval-workflows/${id}`); load(); };
  const ROLES = ["manager","department_head","business_head","director"];
  return (
    <div>
      <div className="bg-white border rounded-xl p-4 mb-4 space-y-3" data-testid="approval-workflow-form">
        <div><Label>Template Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard 3-level" /></div>
        {steps.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-2"><Label>Seq</Label><Input type="number" value={s.sequence} onChange={(e) => setSteps(steps.map((x, idx) => idx === i ? { ...x, sequence: Number(e.target.value) } : x))} /></div>
            <div className="col-span-5"><Label>Approver Role</Label><SelBox v={s.approver_role} set={(v) => setSteps(steps.map((x, idx) => idx === i ? { ...x, approver_role: v } : x))} opts={ROLES} /></div>
            <div className="col-span-3"><Label>SLA days</Label><Input type="number" value={s.sla_days} onChange={(e) => setSteps(steps.map((x, idx) => idx === i ? { ...x, sla_days: Number(e.target.value) } : x))} /></div>
            <button className="col-span-2 text-slate-400 pb-2" onClick={() => setSteps(steps.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={() => setSteps([...steps, { sequence: steps.length + 1, approver_role: "department_head", sla_days: 2, mandatory: true, mode: "sequential" }])}>+ Add step</Button>
        <Button className="w-full" onClick={add} data-testid="approval-workflow-add">Create Template</Button>
      </div>
      <div className="bg-white border rounded-xl divide-y">
        {rows.map((w) => (
          <div key={w.id} className="px-4 py-2.5 text-sm flex items-center gap-3">
            <span className="font-medium">{w.name}</span>
            <span className="text-xs text-slate-500">{w.steps?.map((s) => s.approver_role).join(" → ")}</span>
            <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => del(w.id)}><Trash2 size={14} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-sm">No workflow templates.</div>}
      </div>
    </div>
  );
}

function Recurring() {
  const { users, departments, projects } = useData();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", frequency: "Weekly", start_date: "", owner_id: "", priority: "Medium", due_offset_days: 0, estimated_hours: 2 });
  const load = () => api.get("/recurring-rules").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.title) return toast.error("Title required");
    const p = { ...f, start_date: f.start_date || null, owner_id: f.owner_id || null, due_offset_days: Number(f.due_offset_days), estimated_hours: Number(f.estimated_hours) };
    await api.post("/recurring-rules", p); toast.success("Recurring rule created"); setF({ ...f, title: "" }); load();
  };
  const toggle = async (id) => { await api.patch(`/recurring-rules/${id}/toggle`); load(); };
  return (
    <div>
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="recurring-form">
        <div className="md:col-span-2"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
        <div><Label>Frequency</Label><SelBox v={f.frequency} set={(v) => setF({ ...f, frequency: v })} opts={["Daily","Weekly","Fortnightly","Monthly","Quarterly","Half-Yearly","Annually"]} /></div>
        <div><Label>Start</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
        <div><Label>Owner</Label><SelBox v={f.owner_id} set={(v) => setF({ ...f, owner_id: v })} opts={users.map((u) => [u.id, u.full_name])} ph="Owner" /></div>
        <div><Label>Due +days</Label><Input type="number" value={f.due_offset_days} onChange={(e) => setF({ ...f, due_offset_days: e.target.value })} /></div>
        <Button className="md:col-span-6" onClick={add} data-testid="recurring-add">Add Recurring Rule</Button>
      </div>
      <div className="bg-white border rounded-xl divide-y">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 text-sm flex items-center gap-3">
            <span className="font-medium">{r.title}</span>
            <span className="text-xs text-slate-500">{r.frequency} · created {r.count_created}</span>
            <button className={`ml-auto text-xs rounded-full px-2 py-0.5 ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} onClick={() => toggle(r.id)}>{r.active ? "Active" : "Paused"}</button>
          </div>
        ))}
        {rows.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-sm">No recurring rules.</div>}
      </div>
    </div>
  );
}

function SlaRules() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: "", process: "", duration_days: 2, working_days: true });
  const load = () => api.get("/sla-rules").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!f.name) return toast.error("Name required"); await api.post("/sla-rules", { ...f, duration_days: Number(f.duration_days) }); setF({ ...f, name: "" }); load(); };
  const del = async (id) => { await api.delete(`/sla-rules/${id}`); load(); };
  return (
    <div className="max-w-2xl">
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <div className="col-span-2"><Label>SLA Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Process</Label><Input value={f.process} onChange={(e) => setF({ ...f, process: e.target.value })} /></div>
        <div><Label>Days</Label><Input type="number" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} /></div>
        <Button className="col-span-4" onClick={add} data-testid="sla-add">Add SLA</Button>
      </div>
      <div className="bg-white border rounded-xl divide-y">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 text-sm flex items-center gap-3"><span className="font-medium">{r.name}</span><span className="text-xs text-slate-500">{r.process} · {r.duration_days}d {r.working_days ? "(working)" : ""}</span><button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => del(r.id)}><Trash2 size={14} /></button></div>
        ))}
      </div>
    </div>
  );
}

function Holidays() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: "", date: "" });
  const load = () => api.get("/holidays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!f.name || !f.date) return toast.error("Name & date required"); await api.post("/holidays", f); setF({ name: "", date: "" }); load(); };
  const del = async (id) => { await api.delete(`/holidays/${id}`); load(); };
  return (
    <div className="max-w-xl">
      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
        <div><Label>Holiday</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <Button onClick={add} data-testid="holiday-add">Add</Button>
      </div>
      <div className="bg-white border rounded-xl divide-y">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 text-sm flex items-center gap-3">{r.name}<span className="text-xs text-slate-500">{fmtDate(r.date)}</span><button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => del(r.id)}><Trash2 size={14} /></button></div>
        ))}
        {rows.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-sm">No holidays configured.</div>}
      </div>
    </div>
  );
}

function SelBox({ v, set, opts, ph }) {
  const norm = opts.map((o) => (Array.isArray(o) ? o : [o, o]));
  return (
    <Select value={v} onValueChange={set}>
      <SelectTrigger><SelectValue placeholder={ph || ""} /></SelectTrigger>
      <SelectContent>{norm.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Settings() {
  const [s, setS] = useState({ organization_name: "", session_timeout_minutes: 720 });
  useEffect(() => { api.get("/settings").then((r) => setS({ ...s, ...r.data })); }, []);
  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };
  return (
    <div className="space-y-6">
      <div className="max-w-xl bg-white border rounded-xl p-5 space-y-3">
        <div><Label>Organization Name</Label><Input value={s.organization_name || ""} onChange={(e) => setS({ ...s, organization_name: e.target.value })} /></div>
        <div><Label>Session Timeout (minutes)</Label><Input type="number" value={s.session_timeout_minutes || 720} onChange={(e) => setS({ ...s, session_timeout_minutes: Number(e.target.value) })} /></div>
        <Button onClick={save} data-testid="save-settings">Save Settings</Button>
      </div>
      <TypographySettings />
    </div>
  );
}

function TypographySettings() {
  const [typo, setTypo] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setTypo(r.data?.typography || DEFAULT_TYPOGRAPHY)).catch(() => setTypo(DEFAULT_TYPOGRAPHY)); }, []);
  useEffect(() => { if (typo) applyTypography(typo); }, [typo]); // live preview
  if (!typo) return null;
  const set = (g, k, v) => setTypo((t) => ({ ...t, [g]: { ...(t[g] || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { typography: typo }); toast.success("Typography saved for all users"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="typography-settings">
      <div>
        <div className="font-semibold text-slate-900">Typography / Font Settings</div>
        <p className="text-xs text-slate-500 mt-0.5">Global app typography. Changes preview instantly on this screen; Save applies them for everyone.</p>
      </div>
      {TYPO_GROUPS.map((g) => (
        <div key={g.key} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end border-t border-slate-100 pt-3" data-testid={`typo-group-${g.key}`}>
          <div className="text-sm font-medium text-slate-700 col-span-2 md:col-span-1">{g.label}</div>
          <div>
            <Label className="text-xs">Font</Label>
            <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={typo[g.key]?.family || "Poppins"} onChange={(e) => set(g.key, "family", e.target.value)} data-testid={`typo-${g.key}-family`}>
              {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Weight</Label>
            <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={typo[g.key]?.weight || "400"} onChange={(e) => set(g.key, "weight", e.target.value)} data-testid={`typo-${g.key}-weight`}>
              {["400", "500", "600", "700"].map((w) => <option key={w} value={w}>{w} — {{ 400: "Regular", 500: "Medium", 600: "SemiBold", 700: "Bold" }[w]}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Size (px)</Label>
            <Input type="number" min="8" max="48" placeholder="default" value={typo[g.key]?.size || ""} onChange={(e) => set(g.key, "size", e.target.value)} data-testid={`typo-${g.key}-size`} />
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <Input type="color" className="h-9 p-1 cursor-pointer" value={typo[g.key]?.color || "#0f172a"} onChange={(e) => set(g.key, "color", e.target.value)} data-testid={`typo-${g.key}-color`} />
          </div>
        </div>
      ))}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="typo-preview">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Preview</div>
        <div className="tm-heading text-lg text-slate-900">Workspace Name (heading)</div>
        <div className="tm-body text-sm text-slate-600 mt-1">Body / metadata preview text</div>
        <div className="flex items-center gap-3 mt-3">
          <span className="tm-badge text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-600">Active</span>
          <button className="tm-btn text-sm border border-slate-300 rounded-lg px-3 py-1.5 bg-white">Button</button>
          <span className="tm-nav text-sm text-slate-700">Menu item</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="typo-save">{saving ? "Saving…" : "Save Typography"}</Button>
        <Button variant="outline" onClick={() => setTypo(DEFAULT_TYPOGRAPHY)} data-testid="typo-reset">Reset to Default</Button>
      </div>
    </div>
  );
}
